import { useCallback, useEffect, useState } from 'react';
import { normalizeCapacityRules, DEFAULT_CAPACITY_RULES } from './capacityCalc';
import { leesCache } from './planningCache';
import { haalRegels, sleutels } from './planningBronnen';

/**
 * De actieve capaciteitsregels (standaard + wat de planner heeft aangepast).
 *
 * Elke pagina die bezetting toont rekent hiermee, zodat Planner To-Do, Capaciteit
 * en de uitleg-panelen nooit uit elkaar lopen. Fail-soft: gaat de call stuk, dan
 * rekenen we door met de ingebouwde standaard i.p.v. een lege pagina te tonen.
 */
export function useCapacityRules() {
  // Uit de gedeelde planningcache als die er is: wisselen tussen Rooster en Capaciteit
  // rekent dan meteen met de goede regels in plaats van eerst met de standaard.
  const [rules, setRules] = useState(() => {
    const bewaard = leesCache(sleutels.regels());
    return bewaard ? normalizeCapacityRules(bewaard.waarde?.rules) : DEFAULT_CAPACITY_RULES;
  });
  const [loading, setLoading] = useState(() => !leesCache(sleutels.regels()));
  const [error, setError] = useState(null); // leesbare fout; de schermen kiezen zelf hoe ze die tonen

  // `reload()` (na opslaan in CapaciteitsRegels) haalt altijd vers op; de eerste keer mag
  // de cache antwoorden, met een stille verversing op de achtergrond.
  const laad = useCallback(async (forceer) => {
    try {
      const d = await haalRegels({
        forceer,
        bijVerversing: (nieuw) => setRules(normalizeCapacityRules(nieuw?.rules)),
      });
      setRules(normalizeCapacityRules(d.rules));
      setError(null);
    } catch (e) {
      // Niet stil: fail-soft met de standaardregels, maar wél zichtbaar in de console + error-state.
      console.warn('[useCapacityRules]', e.message);
      setError(e.message);
      setRules(DEFAULT_CAPACITY_RULES);
    } finally {
      setLoading(false);
    }
  }, []);
  const reload = useCallback(() => laad(true), [laad]);

  useEffect(() => { laad(false); }, [laad]);

  return { rules, loading, error, reload, setRules };
}
